/* eslint-disable no-async-promise-executor */
import { canvasRequest } from '$/api/canvasRquest';
import APP from '$/api/api';
import { roboneoApi } from '$/api/request';
import { useLanguageStore } from '$/store/languageStore';
import type { NodeMaterialItem } from '$/domains/material/type';
import useSopStore from '$/store/useSopStore';
import useFlowStore from '$/store/useFlowStore';
import { LanguageCode } from '$/store/languageStore/types';
import { FlowPartType, InputOutputEnum } from '../node/type';
import {
  InputItem, McpCategoriesType, McpType, PortItem, Parameters,
  ToolsCountCostParams,
  ToolsCountCostResponse,
} from './type';

// 1 图片 2 视频 3 网页 4 gif 5 文本
export const portPutTypeByResultTypeMap = {
  1: FlowPartType.IMAGE,
  2: FlowPartType.VIDEO,
  4: FlowPartType.GIF,
  5: FlowPartType.TEXT,
};

/**
 * 根据参数对象和当前语言代码，获取指定key的国际化字符串。
 *
 * @param {Parameters} parameters 参数对象
 * @param {string} key 需要获取的参数key
 * @param {string} apiName parameters[key]为对象时，所需使用的api字段名
 * @returns {string} 获取到的国际化字符串（如name_en/name_pt/name_cn等），获取失败时返回空字符串
 */

function getStrByLanguageCode(parameters: Parameters, key: string, apiName: string) {
  if (!parameters || !parameters[key]) return '';
  const { languageCode: langCode } = useLanguageStore.getState().languageStore;
  // 优先查找第二层的翻译。如果未找到默认走第一层。
  const transData = 'name_en' in ((parameters as any)[key][apiName] ?? {})
    ? (parameters as any)[key][apiName]
    : parameters[key];

  const lang = langCode === LanguageCode.kor ? 'ko' : langCode;

  const name = transData?.[`name_${lang}`] || transData?.name_cn || '';
  return name;
}

function getFeParameterType(att_name: string, fe_parameter_type: string) {
  const { fromAdmin } = useSopStore.getState();
  if (fromAdmin && !fe_parameter_type) {
    const attNameTypeOptiopns: Record<string, string[]> = {
      text: ['prompt', 'lyrics', 'text'],
      image: ['image_url', 'image_list'],
      video: ['video_url', 'video_list'],
      audio: ['music_url'],
    };
    let feParameterType = '';
    Object.keys(attNameTypeOptiopns).forEach((key) => {
      const hasAttName = attNameTypeOptiopns[key].some((item) => att_name.includes(item));
      if (hasAttName) {
        feParameterType = key;
      }
    });
    return feParameterType;
  }
  return fe_parameter_type;
}

const getTag = (tag: string): InputOutputEnum => {
  return tag ? (tag.split('_').pop()?.toLowerCase() as InputOutputEnum) : '' as InputOutputEnum;
};

// 获取输出标签
const getOutPutTag = (tags: string[]): InputOutputEnum => {
  const tag = tags?.[tags.length - 1]?.toLowerCase();
  return tag ? getTag(tag) : '' as InputOutputEnum;
};

const getInputTags = (tags: string[]): InputOutputEnum[] => {
  return tags?.slice(0, -1)?.map((el: string) => getTag(el));
};

/**
 * 从 MCP 配置中提取默认参数，用于调用 toolsCountCost
 * @returns params 映射后的参数（原逻辑）；unmapped 未映射对象 attName -> value
 */
function extractDefaultParamsForCost(item: any): {
  params: {
    id: string;
    tool_name: string;
    video_duration?: string;
    size?: string;
    resolution?: string;
  };
  unmapped: Record<string, any>;
} {
  const apiName = item.api_name;
  const allInputs = [
    ...(item.input_alias.parameter_input_alias || []),
    ...(item.input_alias.media_input_alias || []),
    ...(item.input_alias.params_input_alias || []),
  ];

  const params: {
    id: string;
    tool_name: string;
    video_duration?: string;
    size?: string;
    resolution?: string;
  } = {
    id: apiName,
    tool_name: apiName,
  };

  const unmapped: Record<string, any> = {
    id: apiName,
    tool_name: apiName,
  };

  allInputs.forEach((input: any) => {
    const attName = input.att_name;
    const defaultValue = input.fe_default ?? input.att_default;
    unmapped[attName] = defaultValue != null ? String(defaultValue) : '';

    if (attName === 'video_duration' || attName === 'duration') {
      params.video_duration = defaultValue ? String(defaultValue) : '';
    } else if (attName === 'ratio') {
      params.size = defaultValue ? String(defaultValue) : '';
    } else if (attName === 'size') {
      params.resolution = defaultValue ? String(defaultValue) : '';
    }
  });

  return { params, unmapped };
}

/**
 * 节点工作流配置数据转化
 *
 * @export
 * @param {*} data
 * @return {*}
 */
export function getMCPConfigTransform(data: any) {
  try {
    const mcpConfigList: McpType[] = [];
    const mcpCategoriesList: McpCategoriesType[] = [];
    const mcpNameMap: Record<string, string> = {};
    const { parameters, mcp_list, effect_show_type } = data.data;
    const { fromAdmin } = useSopStore.getState();
    const mcpList = fromAdmin
      ? mcp_list.map((item: any) => (item.params
        ? {
          ...JSON.parse(item.params),
          result_type: item.result_type,
          mcp_name: item.name,
          config_index: item.config_index,
        }
        : item))
      : mcp_list;
    mcpList.forEach((item: any) => {
      const paramsInputConfig: InputItem[] = [];
      const inputPortConfig: PortItem[] = [];
      ([...(item.input_alias.parameter_input_alias || []), ...(item.input_alias.media_input_alias || []), ...(item.input_alias.params_input_alias || [])]).forEach((input: any) => {
        if (input.fe_conceal) return;
        // prompt_name,不暴露给用户，全都自动走扩写
        if (input.att_name === 'prompt_name') return;
        const feParameterType = getFeParameterType(input.att_name, input.fe_parameter_type);
        if (feParameterType) {
        // 输入节点类型
          const defaultMin = input.att_required ? 1 : 0;
          let sourcePortType: PortItem['sourcePortType'] = FlowPartType.TEXT;
          if (feParameterType === 'image') {
            sourcePortType = FlowPartType.IMAGE;
          }
          if (feParameterType === 'video') {
            sourcePortType = FlowPartType.VIDEO;
          }
          if (feParameterType === 'audio') {
            sourcePortType = FlowPartType.AUDIO;
          }
          let min = input.fe_min_items ?? input.att_minItems ?? input.minItems;
          if (typeof min !== 'number') {
            min = input.min_num;
          }
          let max = input.fe_max_items ?? input.att_maxItems ?? input.maxItems;
          if (typeof max !== 'number') {
            max = input.maximum;
          }
          const promptTip = getStrByLanguageCode(parameters, input.fe_inputport_prompt_tip, item.api_name);
          inputPortConfig.push({
            sourcePortType,
            port_name: input.att_name,
            att_type: input.att_type,
            minNum: typeof min === 'number' ? min : defaultMin,
            maxNum: max || 1,
            promptTip,
          });
        }
        else if (input.fe_enum?.length || input.att_enum?.length) {
        // 下拉框
          let suffix = '';
          if (input.att_name === 'video_duration') {
            suffix = getStrByLanguageCode(parameters, 's', item.api_name);
          }
          // 下拉框
          paramsInputConfig.push({
            formType: 'select',
            key: input.att_name,
            label: getStrByLanguageCode(parameters, input.att_name, item.api_name) || input.att_name,
            defaultValue: (input.fe_default || input.att_default),
            isRequired: input.att_required,
            enum: (input.fe_enum || input.att_enum || []).map((enumItem: any) => ({
              label: `${getStrByLanguageCode(parameters, enumItem, item.api_name)
                ? getStrByLanguageCode(parameters, enumItem, item.api_name)
                : enumItem}${suffix}`,
              value: enumItem,
            })),
            valueType: input.att_type,
            relateDisabled: input?.fe_disable,
          });
        }
        else if (input.fe_searchable || input.att_type === 'string') {
          const effect = item.effect?.length ? item.effect[0] : '';

          // 下拉搜索
          paramsInputConfig.push({
            formType: 'searchSelect',
            key: input.att_name,
            label: getStrByLanguageCode(parameters, input.att_name, item.api_name) || input.att_name,
            isRequired: input.att_required,
            valueType: input.att_type,
            materialType: effect,
            isOpenModal: effect_show_type[effect] === 'modal',
            relateDisabled: input?.fe_disable,
          });
        }
        else if (input.att_type === 'number') {
          let min = input.fe_minimum ?? input.att_minimum ?? input.minimum;
          if (typeof min !== 'number') {
            min = input.min_num;
          }
          let max = input.fe_maximum ?? input.att_maximum ?? input.maximum;
          if (typeof max !== 'number') {
            max = input.maximum;
          }
          // 数字输入框
          paramsInputConfig.push({
            formType: 'number',
            key: input.att_name,
            label: getStrByLanguageCode(parameters, input.att_name, item.api_name) || input.att_name,
            defaultValue: input.fe_default || input.att_default || 0,
            isRequired: input.att_required,
            max: typeof max === 'number' ? max : 100,
            min: typeof min === 'number' ? min : 0,
            unit: input.fe_min_unit,
            relateDisabled: input?.fe_disable,
          });
        }
      });

      const outPutPortType = portPutTypeByResultTypeMap[item.result_type as keyof typeof portPutTypeByResultTypeMap];

      const newItem: McpType = {
        apiName: item.api_name,
        name: item.name || '',
        token: item.token,
        hover: item.hover || '',
        inputPortConfig,
        paramsInputConfig,
        outPutPortType,
        result_type: item.result_type, // 只有fromAdmin为true时才有result_type
        config_index: item.config_index, // 只有fromAdmin为true时才有config_index
        mcp_name: item.mcp_name, // 只有fromAdmin为true时才有mcp_name
      };

      mcpConfigList.push(newItem);
    });

    data.data.tree?.forEach((treeItem: any) => {
      if (treeItem.name === 'MCP') {
        const convert = (item: any) => {
          const children = item.children ? item.children.map((child: any) => convert(child)) : undefined;
          if (!item.children) {
            mcpNameMap[item.name] = item.id;
          }
          const newItem: McpCategoriesType = {
            id: `${item.id}`,
            name: item.smartcat_name || item.name,
            iconfont: item.iconfont,
            description: item.smartcat_description || item.description || '',
            outPutTag: getOutPutTag(item.smartcat_tags),
            inputTags: getInputTags(item.smartcat_tags) || [],
            mcpConfig: {
              label: item.smartcat_label || item.label || '',
              mcpAPiNameList: item.linkedTools || [],
            },
            children,
          };
          return newItem;
        };
        treeItem.children.forEach((item: any) => {
          const newItem = convert(item);
          mcpCategoriesList.push(newItem);
        });
      }
    });

    return {
      mcpConfigList,
      mcpCategoriesList,
      materialList: data.data.material_list,
      mcpNameMap,
      // 返回原始 mcpList，用于后续提取默认参数
      rawMcpList: mcpList,
    };
  } catch (error) {
    console.log('getMCPConfigTransform error', error);
    return {
      mcpConfigList: [],
      mcpCategoriesList: [],
      materialList: data.data.material_list,
      mcpNameMap: {},
      rawMcpList: [],
    };
  }
}

/**
 * 获取节点工作流配置
 *
 * @export
 * @return {*}
 */
export async function getMcpList() {
  return canvasRequest.request(
    {
      method: 'GET',
      url: '/workflow/canvas/config.json',
    },
  ).then(async ({ data = {} }) => {
    let mcpConfigList: McpType[] = [];
    let mcpCategoriesList: McpCategoriesType[] = [];
    let mcpNameMap: Record<string, string> = {};
    // const labelMapBylanguage = labelMap[languageCode];
    if (data && data.code === 0 && data.data.mcp_list) {
      const result = getMCPConfigTransform(data);
      mcpConfigList = result.mcpConfigList;
      mcpCategoriesList = result.mcpCategoriesList;
      mcpNameMap = result.mcpNameMap;

      // 批量获取真实的 token 值
      try {
        // 获取 roomId
        const { roomId } = useFlowStore.getState();
        if (roomId && result.rawMcpList && result.rawMcpList.length > 0) {
          // 提取所有 MCP 配置的默认参数，每个 item 内带 params（保留原映射）；unmapped 仅从 extractDefaultParamsForCost 返回供他用
          const costItems = result.rawMcpList.map((item: any) => {
            const { params: p, unmapped } = extractDefaultParamsForCost(item);
            return {
              id: p.id,
              tool_name: p.tool_name,
              video_duration: p.video_duration ? Number(p.video_duration) : 0,
              size: p.size || '',
              resolution: p.resolution || '',
              params: unmapped,
            };
          });

          // 调用 toolsCountCost 获取真实的 cost
          const costParams: ToolsCountCostParams = {
            room_id: roomId,
            items: costItems,
          };

          const costResult: ToolsCountCostResponse = await toolsCountCost(costParams);

          // 根据返回结果更新每个 MCP 配置的 token
          if (costResult.items && Array.isArray(costResult.items)) {
            const costMap = new Map<string, number>();
            costResult.items.forEach((item) => {
              if (item.id && typeof item.cost === 'number') {
                costMap.set(item.id, item.cost);
              }
            });

            // 更新 mcpConfigList 中的 token（创建新数组避免直接修改）
            mcpConfigList = mcpConfigList.map((mcpConfig) => {
              const cost = costMap.get(mcpConfig.apiName);
              if (cost !== undefined) {
                return { ...mcpConfig, token: cost };
              }
              return mcpConfig;
            });
          }
        }
      } catch (error) {
        console.error('[getMcpList] 批量获取 token 失败:', error);
        // 如果获取失败，使用原始 token 值
      }
    }
    return {
      mcpConfigList,
      mcpCategoriesList,
      mcpNameMap,
    };
  });
}

/**
 * 分享节点配置
 *
 * @export
 * @param {string} code
 * @return {*}
 */
export async function getWorkFlowShareConfig(code: string) {
  return new Promise<{
    mcpConfigList: McpType[];
    mcpCategoriesList: McpCategoriesType[];
    materialList: NodeMaterialItem[];
  }>(async (resolve, reject) => {
    const response = await canvasRequest.request({
      params: {
        code,
      },
      method: 'GET',
      url: APP.roboneo.api.shareConfig,
    });
    if (response.status === 200 && response.data.code === 0) {
      const result = getMCPConfigTransform(response.data);
      resolve(result);
      return;
    }
    reject(response);
  });
}

/**
 * 动态计算成本
 * https://api-mock.meitu-int.com/project/1897/interface/api/216989
 *
 * @export
 * @param {ToolsCountCostParams} params
 * @return {*}  {Promise<ToolsCountCostResponse>}
 */
export async function toolsCountCost(params: ToolsCountCostParams): Promise<ToolsCountCostResponse> {
  const response = await roboneoApi.request({
    data: {
      parameter: {
        path_scene: 'countcost',
        ...params,
      },
    },
    method: 'POST',
    url: APP.roboneo.api.path,
  });
  return new Promise((resolve, reject) => {
    if (response.data.error_code === 0) {
      resolve(response.data.parameter);
    } else {
      reject(response.data);
    }
  });
}
